import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useShop } from "../contexts/ShopContext";
import SortableTable from "../components/SortableTable";
import { Card, CardContent } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { User, Phone, IndianRupee, ShoppingBag } from "lucide-react";

export default function Customers() {
  const { shops } = useShop();
  const [shopFilter, setShopFilter] = useState("all");
  const [customers, setCustomers] = useState([]);
  const [selected, setSelected] = useState(null); // customer
  const [bills, setBills] = useState([]);
  const [billDetail, setBillDetail] = useState(null);

  const load = async () => {
    const url = shopFilter === "all" ? "/customers" : `/customers?shop_id=${shopFilter}`;
    const r = await api.get(url);
    setCustomers(r.data);
  };
  useEffect(() => { load(); }, [shopFilter]);

  const openCustomer = async (c) => {
    setSelected(c);
    const r = await api.get(`/customers/${encodeURIComponent(c.phone)}/bills`);
    setBills(r.data);
  };

  const customerCols = [
    { key: "name", label: "Name", render: (r) => <span className="font-medium">{r.name || "-"}</span> },
    { key: "phone", label: "Phone", accessor: (r) => r.phone, render: (r) => <span className="font-mono text-xs">{r.phone}</span> },
    { key: "bill_count", label: "Bills", align: "right", accessor: (r) => r.bill_count, render: (r) => <span className="font-mono">{r.bill_count}</span> },
    { key: "total_spent", label: "Total Spent", align: "right", accessor: (r) => r.total_spent, render: (r) => <span className="font-mono text-terracotta font-bold">₹{Math.round(r.total_spent)}</span> },
    { key: "lp_balance", label: "LP Balance", align: "right", accessor: (r) => r.lp_balance || 0, render: (r) => <span className="font-mono font-bold text-moss" data-testid={`lp-${r.phone}`}>{r.lp_balance || 0}</span> },
    { key: "last_visit", label: "Last Visit", accessor: (r) => r.last_visit, render: (r) => <span className="text-xs text-muted-foreground">{new Date(r.last_visit).toLocaleString()}</span> },
  ];

  const totalRevenue = customers.reduce((s, c) => s + c.total_spent, 0);
  const totalBills = customers.reduce((s, c) => s + c.bill_count, 0);

  return (
    <div className="p-8 overflow-y-auto" data-testid="customers-page">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="label-overline text-muted-foreground">Customer Database</div>
          <h1 className="font-heading text-4xl font-bold text-ink">Customers</h1>
        </div>
        <div className="w-48">
          <Select value={shopFilter} onValueChange={setShopFilter}>
            <SelectTrigger data-testid="customers-shop-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Shops</SelectItem>
              {shops.map((s) => (<SelectItem key={s.shop_id} value={s.shop_id}>{s.name}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-6">
        <Card className="border-border shadow-none"><CardContent className="p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded bg-terracotta/10 flex items-center justify-center"><User size={18} className="text-terracotta"/></div>
          <div><div className="label-overline text-muted-foreground">Total Customers</div><div className="font-heading text-2xl font-bold">{customers.length}</div></div>
        </CardContent></Card>
        <Card className="border-border shadow-none"><CardContent className="p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded bg-moss/10 flex items-center justify-center"><ShoppingBag size={18} className="text-moss"/></div>
          <div><div className="label-overline text-muted-foreground">Total Bills</div><div className="font-heading text-2xl font-bold">{totalBills}</div></div>
        </CardContent></Card>
        <Card className="border-border shadow-none"><CardContent className="p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded bg-ochre/20 flex items-center justify-center"><IndianRupee size={18} className="text-ochre"/></div>
          <div><div className="label-overline text-muted-foreground">Lifetime Revenue</div><div className="font-heading text-2xl font-bold">₹{totalRevenue.toFixed(0)}</div></div>
        </CardContent></Card>
      </div>

      <SortableTable
        testid="customers-table"
        columns={customerCols}
        data={customers.map((c) => ({ ...c, _onClick: () => openCustomer(c) }))}
        searchable={["name", "phone", "address"]}
        emptyText="No customers yet. Customer details added during billing will appear here."
      />

      {/* Customer detail */}
      <Dialog open={!!selected} onOpenChange={(o) => { if (!o) { setSelected(null); setBills([]); } }}>
        <DialogContent className="max-w-3xl" data-testid="customer-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><User size={18}/> {selected?.name || "Customer"}</DialogTitle>
          </DialogHeader>
          {selected && (
            <>
              <div className="grid grid-cols-4 gap-4 mb-4 bg-sand p-4 rounded">
                <div><div className="label-overline text-muted-foreground">Phone</div><div className="font-mono mt-1 flex items-center gap-1"><Phone size={12}/>{selected.phone}</div></div>
                <div><div className="label-overline text-muted-foreground">Total Bills</div><div className="font-heading text-2xl font-bold">{selected.bill_count}</div></div>
                <div><div className="label-overline text-muted-foreground">Total Spent</div><div className="font-heading text-2xl font-bold text-terracotta">₹{Math.round(selected.total_spent)}</div></div>
                <div data-testid="customer-lp-detail">
                  <div className="label-overline text-muted-foreground">Loyalty Points</div>
                  <div className="font-heading text-2xl font-bold text-moss">{selected.lp_balance || 0}</div>
                  <div className="text-[10px] text-muted-foreground">
                    earned {selected.lp_earned || 0} · used {selected.lp_redeemed || 0}
                  </div>
                </div>
              </div>
              <div className="font-heading font-bold mb-2">Bill History</div>
              <div className="max-h-80 overflow-y-auto scroll-thin border border-border rounded">
                <table className="w-full text-sm">
                  <thead className="bg-surface-alt sticky top-0">
                    <tr><th className="px-3 py-2 text-left">Bill No</th><th className="px-3 py-2 text-left">Date</th><th className="px-3 py-2 text-left">Shop</th><th className="px-3 py-2 text-right">Items</th><th className="px-3 py-2 text-right">Amount</th><th></th></tr>
                  </thead>
                  <tbody>
                    {bills.map((b) => (
                      <tr key={b.bill_id} className="border-t border-border hover:bg-sand cursor-pointer" onClick={() => setBillDetail(b)} data-testid={`customer-bill-${b.bill_id}`}>
                        <td className="px-3 py-2 font-mono text-xs">{b.bill_no}</td>
                        <td className="px-3 py-2 text-xs">{new Date(b.created_at).toLocaleDateString()}</td>
                        <td className="px-3 py-2"><Badge variant="outline" className="text-xs">{b.shop_id}</Badge></td>
                        <td className="px-3 py-2 text-right">{b.items.length}</td>
                        <td className="px-3 py-2 text-right font-mono font-bold">₹{Math.round(b.net_total)}</td>
                        <td className="px-3 py-2 text-xs text-terracotta">View →</td>
                      </tr>
                    ))}
                    {bills.length === 0 && <tr><td colSpan={6} className="text-center py-6 text-muted-foreground">No bills</td></tr>}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Bill items detail */}
      <Dialog open={!!billDetail} onOpenChange={(o) => { if (!o) setBillDetail(null); }}>
        <DialogContent data-testid="bill-items-dialog">
          <DialogHeader><DialogTitle className="font-heading">Bill {billDetail?.bill_no}</DialogTitle></DialogHeader>
          {billDetail && (
            <div className="space-y-3">
              <div className="text-xs text-muted-foreground">{new Date(billDetail.created_at).toLocaleString()} · {billDetail.payment_method.toUpperCase()} · {billDetail.shop_id}</div>
              <table className="w-full text-sm border border-border rounded">
                <thead className="bg-surface-alt"><tr><th className="px-2 py-1 text-left">Product</th><th className="px-2 py-1 text-right">MRP</th><th className="px-2 py-1 text-right">Qty</th><th className="px-2 py-1 text-right">Rate</th><th className="px-2 py-1 text-right">Amount</th></tr></thead>
                <tbody>
                  {billDetail.items.map((it, i) => (
                    <tr key={i} className="border-t border-border">
                      <td className="px-2 py-1">{it.name}</td>
                      <td className="px-2 py-1 text-right font-mono">₹{Math.round(it.mrp)}</td>
                      <td className="px-2 py-1 text-right">{it.qty}</td>
                      <td className="px-2 py-1 text-right font-mono">₹{Math.round(it.rate)}</td>
                      <td className="px-2 py-1 text-right font-mono">₹{Math.round(it.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="flex justify-between pt-2 border-t border-border">
                <span className="font-heading font-bold">Net Total</span>
                <span className="font-mono text-2xl font-bold text-terracotta">₹{Math.round(billDetail.net_total)}</span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
